import { z } from "zod";

// Workflow types
export const askPunttWorkflowTypeSchema = z.enum([
  "compliance_review",
  "artwork_review",
  "brief_creation",
  "claim_generation",
  "general",
  "project",
]);
export type AskPunttWorkflowType = z.infer<typeof askPunttWorkflowTypeSchema>;

// Source result in search progress
export const askPunttSourceResultSchema = z.object({
  title: z.string(),
  url: z.string().optional(),
  type: z.string().optional(), // e.g. "document", "ticket", "regulation"
});
export type AskPunttSourceResult = z.infer<typeof askPunttSourceResultSchema>;

// Search progress for a single source
export const askPunttSearchProgressSchema = z.object({
  source: z.string(), // e.g. "Puntt Regulatory Database", "Danone's Claims Data", "Wrike"
  status: z.enum(["searching", "done", "error"]),
  resultCount: z.number().optional(),
  results: z.array(askPunttSourceResultSchema).optional(),
});
export type AskPunttSearchProgress = z.infer<
  typeof askPunttSearchProgressSchema
>;

// Alternative claim suggestion
export const askPunttAlternativeSchema = z.object({
  riskLevel: z.enum(["low", "medium", "high"]),
  claim: z.string(),
  why: z.string().optional(),
  precedent: z.string().optional(),
  status: z.string().optional(),
  approvalPath: z.string().optional(),
  tradeOff: z.string().optional(),
});
export type AskPunttAlternative = z.infer<typeof askPunttAlternativeSchema>;

// Source citation
export const askPunttSourceCitationSchema = z.object({
  label: z.string(), // e.g. "Puntt Regulatory Database", "EUR Lex", "ASA"
  url: z.string().optional(),
  type: z.enum(["regulatory", "internal", "external"]).optional(),
});
export type AskPunttSourceCitation = z.infer<
  typeof askPunttSourceCitationSchema
>;

// Structured content for rich AI responses
export const askPunttStructuredContentSchema = z.object({
  riskLevel: z.enum(["low", "medium", "high"]).optional(),
  confidence: z.enum(["low", "medium", "high"]).optional(),
  shortAnswer: z.string().optional(),
  regulatoryAnalysis: z.string().optional(),
  alternatives: z.array(askPunttAlternativeSchema).optional(),
  sources: z.array(askPunttSourceCitationSchema).optional(),
  timeSaved: z
    .object({
      summary: z.string(), // e.g. "2-3 Weeks"
      breakdown: z.array(
        z.object({
          week: z.string(),
          description: z.string(),
        }),
      ),
    })
    .optional(),
});
export type AskPunttStructuredContent = z.infer<
  typeof askPunttStructuredContentSchema
>;

// Attachment (S3 reference)
export const askPunttAttachmentSchema = z.object({
  key: z.string(),
  source: z.string().url(),
  type: z.enum(["image", "video", "file"]),
  name: z.string().optional(),
});
export type AskPunttAttachment = z.infer<typeof askPunttAttachmentSchema>;

// Agent step (thinking, tool call, tool result) for real-time activity log
export const askPunttAgentStepSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("thinking"),
    content: z.string(),
    timestamp: z.string(),
  }),
  z.object({
    type: z.literal("tool_call"),
    tool: z.string(),
    displayName: z.string(),
    statusName: z.string().optional(),
    args: z.record(z.unknown()).optional(),
    description: z.string().optional(),
    timestamp: z.string(),
  }),
  z.object({
    type: z.literal("tool_result"),
    tool: z.string(),
    displayName: z.string(),
    summary: z.string(),
    resultCount: z.number().optional(),
    timestamp: z.string(),
  }),
]);
export type AskPunttAgentStep = z.infer<typeof askPunttAgentStepSchema>;

// Canvas block types for rich structured output
export const askPunttCanvasBlockSchema: z.ZodType = z.lazy(() =>
  z.discriminatedUnion("type", [
    z.object({
      type: z.literal("heading"),
      level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
      text: z.string(),
    }),
    z.object({ type: z.literal("paragraph"), text: z.string() }),
    z.object({
      type: z.literal("badge"),
      label: z.string(),
      color: z.enum(["green", "amber", "red", "blue", "gray"]),
      size: z.enum(["sm", "md", "lg"]).optional(),
    }),
    z.object({
      type: z.literal("card"),
      title: z.string(),
      subtitle: z.string().optional(),
      badge: z.record(z.unknown()).optional(),
      children: z.array(z.record(z.unknown())),
    }),
    z.object({
      type: z.literal("grid"),
      columns: z.number(),
      children: z.array(z.record(z.unknown())),
    }),
    z.object({
      type: z.literal("table"),
      headers: z.array(z.string()),
      rows: z.array(z.array(z.string())),
      highlightRules: z.record(z.string()).optional(),
    }),
    z.object({
      type: z.literal("list"),
      ordered: z.boolean(),
      items: z.array(z.record(z.unknown())),
    }),
    z.object({ type: z.literal("divider") }),
    z.object({
      type: z.literal("metric"),
      label: z.string(),
      value: z.string(),
      trend: z.enum(["up", "down", "neutral"]).optional(),
    }),
    z.object({
      type: z.literal("expandable"),
      title: z.string(),
      summary: z.string().optional(),
      children: z.array(z.record(z.unknown())),
    }),
    z.object({
      type: z.literal("section"),
      id: z.string(),
      title: z.string(),
      editable: z.boolean().optional(),
      children: z.array(z.record(z.unknown())),
    }),
    z.object({
      type: z.literal("source_link"),
      title: z.string(),
      url: z.string(),
      linkType: z.string().optional(),
    }),
    z.object({
      type: z.literal("chart"),
      chartType: z.enum(["bar", "pie", "line"]),
      data: z.record(z.unknown()),
    }),
    z.object({
      type: z.literal("callout"),
      variant: z.enum(["info", "warning", "success", "error"]),
      text: z.string(),
    }),
    z.object({
      type: z.literal("file_preview"),
      url: z.string(),
      fileName: z.string(),
      fileType: z.enum(["pdf", "xlsx", "xls", "docx", "doc", "pptx", "ppt"]),
      pageCount: z.number().optional(),
    }),
  ]),
);
export type AskPunttCanvasBlock = z.infer<typeof askPunttCanvasBlockSchema>;

// Canvas content wrapping blocks
export const askPunttCanvasContentSchema = z.object({
  outputType: z.enum([
    "risk_assessment",
    "brief_document",
    "comparison",
    "report",
    "visualization",
    "custom",
  ]),
  blocks: z.array(z.record(z.unknown())),
  rawHtml: z.string().nullable().optional(),
  exportFormats: z.array(z.string()).optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type AskPunttCanvasContent = z.infer<typeof askPunttCanvasContentSchema>;

// Structured question option (for multi-question forms)
export const askPunttQuestionOptionSchema = z.object({
  label: z.string(),
  description: z.string(),
});
export type AskPunttQuestionOption = z.infer<
  typeof askPunttQuestionOptionSchema
>;

// Structured question (used by AskUserQuestion tool)
export const askPunttUserQuestionSchema = z.object({
  question: z.string(),
  header: z.string(),
  options: z.array(askPunttQuestionOptionSchema),
  multiSelect: z.boolean().optional(),
  allowFiles: z.boolean().optional(),
});
export type AskPunttUserQuestion = z.infer<typeof askPunttUserQuestionSchema>;

// Project stage status
export const askPunttProjectStageSchema = z.object({
  name: z.string(),
  status: z.enum(["pending", "active", "done", "blocked", "needs_review"]),
  color: z.enum(["green", "amber", "red", "gray"]),
});
export type AskPunttProjectStage = z.infer<typeof askPunttProjectStageSchema>;

// Project reviewer routing
export const askPunttProjectReviewerSchema = z.object({
  name: z.string(),
  initials: z.string(),
  role: z.string(),
  mode: z.enum(["Parallel", "After"]),
  status: z.enum(["active", "pending", "done", "needs_review"]),
});
export type AskPunttProjectReviewer = z.infer<
  typeof askPunttProjectReviewerSchema
>;

// Project forecast data
export const askPunttProjectForecastSchema = z.object({
  cycleTimeDays: z.number(),
  vsTargetDays: z.number(),
  confidence: z.number().min(0).max(100),
});
export type AskPunttProjectForecast = z.infer<
  typeof askPunttProjectForecastSchema
>;

// Full project data (lives on chat + message level)
export const askPunttProjectDataSchema = z.object({
  name: z.string(),
  markets: z.array(z.string()),
  projectType: z.string(),
  stages: z.array(askPunttProjectStageSchema),
  routing: z.array(askPunttProjectReviewerSchema),
  forecast: askPunttProjectForecastSchema,
  aiRecommendation: z.string().optional(),
  targetDeadline: z.string().optional(),
  completedAt: z.string().optional(),
});
export type AskPunttProjectData = z.infer<typeof askPunttProjectDataSchema>;

// Message
export const askPunttMessageSchema = z.object({
  _id: z.string(),
  chatId: z.string(),
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  status: z.enum(["pending", "completed"]).optional(),
  structuredContent: askPunttStructuredContentSchema.optional(),
  activeSkill: z.string().optional(),
  canvasContent: askPunttCanvasContentSchema.optional(),
  searchProgress: z.array(askPunttSearchProgressSchema).optional(),
  agentSteps: z.array(askPunttAgentStepSchema).optional(),
  attachments: z.array(askPunttAttachmentSchema).optional(),
  viewTicketId: z.string().optional(),
  suggestedResponses: z.array(z.string()).nullable().optional(),
  userQuestions: z.array(askPunttUserQuestionSchema).nullable().optional(),
  projectData: askPunttProjectDataSchema.nullable().optional(),
  streamingContent: z.string().nullable().optional(),
  canvasLoading: z.boolean().nullable().optional(),
  createdAt: z.string(),
});
export type AskPunttMessage = z.infer<typeof askPunttMessageSchema>;

// Chat
export const askPunttChatSchema = z.object({
  _id: z.string(),
  title: z.string(),
  workflowType: askPunttWorkflowTypeSchema,
  enterpriseId: z.string(),
  userId: z.string(),
  enabledConnectors: z.array(z.string()).optional(),
  detectedSkill: z.string().nullable().optional(),
  ticketIds: z.array(z.string()).optional(),
  reviewStatus: z.string().nullable().optional(),
  projectData: askPunttProjectDataSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AskPunttChat = z.infer<typeof askPunttChatSchema>;

// --- Request/Response Schemas ---

// List chats
export const listAskPunttChatsRequestSchema = z.object({
  query: z.object({
    limit: z.coerce.number().optional().default(20),
    offset: z.coerce.number().optional().default(0),
  }),
});
export const listAskPunttChatsResponseSchema = z.object({
  chats: z.array(
    askPunttChatSchema.pick({
      _id: true,
      title: true,
      workflowType: true,
      enabledConnectors: true,
      projectData: true,
      updatedAt: true,
    }),
  ),
  total: z.number(),
});
export type ListAskPunttChatsResponse = z.infer<
  typeof listAskPunttChatsResponseSchema
>;

// Get chat history
export const getAskPunttChatHistoryRequestSchema = z.object({
  params: z.object({
    chatId: z.string(),
  }),
});
export const getAskPunttChatHistoryResponseSchema = z.object({
  chat: askPunttChatSchema,
  messages: z.array(askPunttMessageSchema),
});
export type GetAskPunttChatHistoryResponse = z.infer<
  typeof getAskPunttChatHistoryResponseSchema
>;

// Create chat
export const createAskPunttChatRequestSchema = z.object({
  body: z.object({
    message: z.string(),
    workflowType: askPunttWorkflowTypeSchema.optional().default("general"),
    attachments: z.array(askPunttAttachmentSchema).optional().default([]),
    enabledConnectors: z.array(z.string()).optional().default([]),
  }),
});
export const createAskPunttChatResponseSchema = z.object({
  chat: askPunttChatSchema,
  userMessage: askPunttMessageSchema,
  assistantMessage: askPunttMessageSchema.optional(),
  workflowId: z.string().optional(),
});
export type CreateAskPunttChatResponse = z.infer<
  typeof createAskPunttChatResponseSchema
>;

// Send message
export const sendAskPunttMessageRequestSchema = z.object({
  params: z.object({
    chatId: z.string(),
  }),
  body: z.object({
    message: z.string(),
    attachments: z.array(askPunttAttachmentSchema).optional().default([]),
  }),
});
export const sendAskPunttMessageResponseSchema = z.object({
  userMessage: askPunttMessageSchema,
  workflowId: z.string(),
});
export type SendAskPunttMessageResponse = z.infer<
  typeof sendAskPunttMessageResponseSchema
>;
